import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid} from 'recharts';
import DatePicker from 'react-datepicker';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import {createPortal} from 'react-dom';

export type SalesMetricKey = 'sales' | 'avgDailySales' | 'orders' | 'conversionRate';

export interface SalesStat {
    metric: SalesMetricKey;
    label: string;
    value: string;
}

interface SalesChartPoint {
    label: string;
    value: number;
}

interface SalesSeriesPoint {
    date: string;
    label: string;
    salesAmount: number;
    ordersCount: number;
}

interface SalesTooltipProps {
    active?: boolean;
    payload?: Array<{value: unknown}>;
    label?: string;
    formatValue: (value: number) => string;
}

const SalesTooltip: React.FC<SalesTooltipProps> = ({active, payload, label, formatValue}) => {
    if (active && payload && payload.length) {
        const rawValue = payload[0].value;
        const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        const formatted = Number.isFinite(value) ? formatValue(value) : '—';
        return (
            <div className="rounded-xl border border-[#d7e4e1] bg-white px-4 py-3 shadow-sm text-sm text-[#2b4144]">
                <p className="font-semibold">{label}</p>
                <p className="text-[#2d8473]">{formatted}</p>
            </div>
        );
    }
    return null;
};

export interface SalesReportChartProps {
    reportTabs: string[];
    activeRange: string;
    onSelectRange: (range: string) => void;
    salesStats: SalesStat[];
    salesSeries: SalesSeriesPoint[];
    conversionRate: number | null;
    customRange: [Date | null, Date | null];
    onChangeCustomRange: (range: [Date | null, Date | null]) => void;
}

const formatCurrency = (value: number) => value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});

const formatNumber = (value: number) => value.toLocaleString('en-US');

const formatPercent = (value: number) => value.toLocaleString('en-US', {style: 'percent', maximumFractionDigits: 0});

const parseIsoDate = (value: string) => new Date(`${value}T00:00:00Z`);

const getDaysInMonth = (date: Date) =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();

const SalesReportChart: React.FC<SalesReportChartProps> = ({
    reportTabs,
    activeRange,
    onSelectRange,
    salesStats,
    salesSeries,
    conversionRate,
    customRange,
    onChangeCustomRange,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [popoverStyle, setPopoverStyle] = useState<{top: number; left: number; width: number} | null>(null);
    const [activeMetric, setActiveMetric] = useState<SalesMetricKey>(() => salesStats[0]?.metric ?? 'sales');
    const hasConversionRate = typeof conversionRate === 'number' && Number.isFinite(conversionRate);
    const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);
    const [draftStart, setDraftStart] = useState<Date | null>(customRange[0]);
    const [draftEnd, setDraftEnd] = useState<Date | null>(customRange[1]);
    const [rangeStart, rangeEnd] = isRangePickerOpen
        ? ([draftStart, draftEnd] as const)
        : ([customRange[0], customRange[1]] as const);
    const hasRangeSelection = Boolean(rangeStart && rangeEnd);
    const customRangeLabel =
        rangeStart && rangeEnd
            ? `${rangeStart.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              })} – ${rangeEnd.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              })}`
            : 'Select a start and end date.';

    useEffect(() => {
        if (activeRange !== 'Custom Range') {
            setIsRangePickerOpen(false);
        }
    }, [activeRange]);

    useEffect(() => {
        if (!isRangePickerOpen) {
            setDraftStart(customRange[0]);
            setDraftEnd(customRange[1]);
        }
    }, [customRange, isRangePickerOpen]);

    useEffect(() => {
        if (activeRange === 'Custom Range' && !customRange[0] && !customRange[1]) {
            setIsRangePickerOpen(true);
        }
    }, [activeRange, customRange]);

    const handleApplyRange = () => {
        if (draftStart && draftEnd) {
            onChangeCustomRange([draftStart, draftEnd]);
            setIsRangePickerOpen(false);
        }
    };

    const handleCancelRange = () => {
        setDraftStart(customRange[0]);
        setDraftEnd(customRange[1]);
        setIsRangePickerOpen(false);
    };

    const handleStartChange = (date: Date | null) => {
        setDraftStart(date);
        if (date && draftEnd && draftEnd < date) {
            setDraftEnd(null);
        }
    };

    const handleEndChange = (date: Date | null) => {
        setDraftEnd(date);
    };

    const addMonths = (date: Date, months: number) => {
        const next = new Date(date);
        next.setMonth(next.getMonth() + months);
        return next;
    };

    useEffect(() => {
        if (!isRangePickerOpen) {
            setPopoverStyle(null);
            return;
        }

        const updatePosition = () => {
            const container = containerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            const width = Math.min(rect.width, 720);
            const left = rect.left + window.scrollX + Math.max(0, (rect.width - width) / 2);
            const top = rect.top + window.scrollY + 120;
            setPopoverStyle({top, left, width});
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, {passive: true});
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition);
        };
    }, [isRangePickerOpen]);

    const popoverTarget = typeof document !== 'undefined' ? document.body : null;

    const chartData = useMemo<SalesChartPoint[]>(() => {
        if (!salesSeries.length) return [];

        switch (activeMetric) {
            case 'orders':
                return salesSeries.map((point) => ({label: point.label, value: point.ordersCount}));
            case 'avgDailySales':
                return salesSeries.map((point) => {
                    const date = parseIsoDate(point.date);
                    const days = activeRange === 'Year' ? getDaysInMonth(date) : 1;
                    return {label: point.label, value: days > 0 ? point.salesAmount / days : 0};
                });
            case 'conversionRate':
                return salesSeries.map((point) => ({
                    label: point.label,
                    value: hasConversionRate ? conversionRate : 0,
                }));
            case 'sales':
            default:
                return salesSeries.map((point) => ({label: point.label, value: point.salesAmount}));
        }
    }, [activeMetric, activeRange, conversionRate, hasConversionRate, salesSeries]);

    const maxValue = useMemo(() => {
        if (!chartData.length) return 0;
        const max = Math.max(...chartData.map((point) => point.value));
        switch (activeMetric) {
            case 'conversionRate':
                return Math.min(1, max + 0.05);
            case 'orders':
                return Math.max(1, max + 2);
            case 'avgDailySales':
            case 'sales':
            default:
                return max + 20;
        }
    }, [activeMetric, chartData]);

    const formatValue = useMemo(() => {
        if (activeMetric === 'conversionRate') {
            return (value: number) => (hasConversionRate ? formatPercent(value) : '—');
        }
        if (activeMetric === 'orders') {
            return formatNumber;
        }
        return formatCurrency;
    }, [activeMetric, hasConversionRate]);

    const yAxisFormatter = useMemo(() => {
        if (activeMetric === 'conversionRate') {
            return (value: number) => formatPercent(value);
        }
        if (activeMetric === 'orders') {
            return (value: number) => formatNumber(value);
        }
        return (value: number) => `$${value.toLocaleString()}`;
    }, [activeMetric]);

    return (
        <div ref={containerRef}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <h2 className="text-[27px] font-medium text-mld-text-primary mb-2 ">Sales Reports</h2>
                <button className="text-mld-muted-text hover:text-[#42D4B0]" type="button">
                    View All
                </button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[250px_1fr_1fr] rounded-2xl border border-mld-panel-border bg-mld-menu-background">
                <div className="grid border-r-0 lg:border-r border-mld-panel-border">
                    {salesStats.map((stat) => {
                        const isActive = stat.metric === activeMetric;
                        return (
                            <button
                                key={stat.metric}
                                type="button"
                                onClick={() => setActiveMetric(stat.metric)}
                                aria-pressed={isActive}
                                className={`flex w-full flex-col justify-center p-6 h-26 text-left border-t last:border-b lg:last:border-b-0 first:rounded-t-2xl lg:first:rounded-tr-0 border-mld-panel-border transition-colors first:border-t-0 ${
                                    isActive ? 'bg-white/70' : 'hover:bg-white/40'
                                }`}>
                                <p className="text-mld-text-primary text-2xl">{stat.value}</p>
                                <p className="text-mld-text-secondary">{stat.label}</p>
                            </button>
                        );
                    })}
                </div>
                <div className="flex flex-col gap-4 p-6 lg:col-span-2">
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Sales report ranges">
                        {reportTabs.map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                role="tab"
                                aria-selected={activeRange === tab}
                                aria-controls="provider-sales-report-panel"
                                className={`px-4 py-2 text-sm ${activeRange === tab ? ' underline' : ''}`}
                                onClick={() => onSelectRange(tab)}>
                                {tab}
                            </button>
                        ))}
                    </div>
                    {activeRange === 'Custom Range' && (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-mld-muted-text">
                                <span>{customRangeLabel}</span>
                                <button
                                    type="button"
                                    onClick={() => setIsRangePickerOpen((prev) => !prev)}
                                    className="text-mld-muted-text hover:text-mld-primary">
                                    {isRangePickerOpen ? 'Close' : hasRangeSelection ? 'Change' : 'Select dates'}
                                </button>
                            </div>
                        </div>
                    )}
                    <div
                        id="provider-sales-report-panel"
                        role="region"
                        aria-label="Sales report chart"
                        className="grow p-4 h-56 sm:h-60 lg:h-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{top: 10, right: 20, left: 0, bottom: 0}}>
                                <defs>
                                    <linearGradient id="providerSalesGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#bdebe0" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#effaf5" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#999999" strokeWidth={1} vertical horizontal opacity={0.2} />
                                <XAxis
                                    dataKey="label"
                                    tick={{fill: '#8fa196', fontSize: 12}}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    orientation="right"
                                    domain={[0, maxValue]}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{fill: '#8fa196', fontSize: 12}}
                                    tickFormatter={yAxisFormatter}
                                />
                                <Tooltip
                                    content={<SalesTooltip formatValue={formatValue} />}
                                    cursor={{fill: 'rgba(110,196,186,0.1)'}}
                                />
                                <Area
                                    type="linear"
                                    dataKey="value"
                                    stroke="#6ec4ba"
                                    strokeWidth={2}
                                    fill="url(#providerSalesGradient)"
                                    activeDot={{
                                        r: 4,
                                        fill: '#2d8473',
                                        stroke: '#fff',
                                        strokeWidth: 1,
                                    }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
            {isRangePickerOpen && popoverTarget && popoverStyle
                ? createPortal(
                      <div
                          className="sales-range-popover provider-calendar rounded-2xl border border-[#e1eae3] bg-white/95 p-4 shadow-[0_20px_65px_rgba(29,59,72,0.18)]"
                          style={{
                              position: 'absolute',
                              top: popoverStyle.top,
                              left: popoverStyle.left,
                              width: popoverStyle.width,
                              zIndex: 60,
                          }}>
                          <div className="sales-range-picker">
                              <div>
                                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-mld-muted-text">
                                      From
                                  </p>
                                  <DatePicker
                                      inline
                                      selected={draftStart}
                                      onChange={(date) => handleStartChange(date)}
                                      endDate={draftEnd}
                                      maxDate={draftEnd ?? undefined}
                                      openToDate={draftStart ?? new Date()}
                                      calendarStartDay={0}
                                      weekDayClassName={() => 'provider-calendar__weekday'}
                                      formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
                                      renderCustomHeader={({date, decreaseMonth, increaseMonth}) => (
                                          <div className="flex items-center justify-between px-2 text-mld-text-primary">
                                              <button
                                                  type="button"
                                                  onClick={decreaseMonth}
                                                  aria-label="Previous month"
                                                  className="text-mld-text-primary hover:text-mld-primary">
                                                  <ChevronLeft className="h-6 w-6" />
                                              </button>
                                              <span className="Heading05Bold" style={{fontSize: '1rem'}}>
                                                  {date.toLocaleDateString('en-US', {
                                                      month: 'long',
                                                      year: 'numeric',
                                                  })}
                                              </span>
                                              <button
                                                  type="button"
                                                  onClick={increaseMonth}
                                                  aria-label="Next month"
                                                  className="text-mld-text-primary hover:text-mld-primary">
                                                  <ChevronRight className="h-6 w-6" />
                                              </button>
                                          </div>
                                      )}
                                      calendarClassName="sales-range-calendar"
                                  />
                              </div>
                              <div>
                                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-mld-muted-text">
                                      To
                                  </p>
                                  <DatePicker
                                      inline
                                      selected={draftEnd}
                                      onChange={(date) => handleEndChange(date)}
                                      startDate={draftStart}
                                      minDate={draftStart ?? undefined}
                                      openToDate={draftEnd ?? (draftStart ? addMonths(draftStart, 1) : new Date())}
                                      calendarStartDay={0}
                                      weekDayClassName={() => 'provider-calendar__weekday'}
                                      formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
                                      renderCustomHeader={({date, decreaseMonth, increaseMonth}) => (
                                          <div className="flex items-center justify-between px-2 text-mld-text-primary">
                                              <button
                                                  type="button"
                                                  onClick={decreaseMonth}
                                                  aria-label="Previous month"
                                                  className="text-mld-text-primary hover:text-mld-primary">
                                                  <ChevronLeft className="h-6 w-6" />
                                              </button>
                                              <span className="Heading05Bold" style={{fontSize: '1rem'}}>
                                                  {date.toLocaleDateString('en-US', {
                                                      month: 'long',
                                                      year: 'numeric',
                                                  })}
                                              </span>
                                              <button
                                                  type="button"
                                                  onClick={increaseMonth}
                                                  aria-label="Next month"
                                                  className="text-mld-text-primary hover:text-mld-primary">
                                                  <ChevronRight className="h-6 w-6" />
                                              </button>
                                          </div>
                                      )}
                                      calendarClassName="sales-range-calendar"
                                  />
                              </div>
                          </div>
                          <div className="mt-4 flex flex-wrap gap-2">
                              <button
                                  type="button"
                                  onClick={handleCancelRange}
                                  className="rounded-full border border-[#dfe8e2] bg-white px-4 py-2 text-sm font-semibold text-[#5d7269] hover:border-[#2d8473] hover:text-[#2d8473]">
                                  Cancel
                              </button>
                              <button
                                  type="button"
                                  onClick={handleApplyRange}
                                  disabled={!draftStart || !draftEnd}
                                  className="rounded-full bg-mld-primary px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
                                  Apply
                              </button>
                          </div>
                      </div>,
                      popoverTarget
                  )
                : null}
        </div>
    );
};

export default SalesReportChart;
